import { MaterialSymbol } from "@ui/MaterialSymbol";
import { Pill } from "@ui/Pill";
import { HStack, VStack } from "@ui/Stack";
import { GVTColorSymbol } from "@ui/typography/GVTColorSymbol";
import { MonoText } from "@ui/typography/MonoText";
import { Text } from "@ui/typography/Text";
import { getPlatformColorGlyph } from "@utils/platform";
import { createEffect, For, Show } from "solid-js";
import { isDirectUploadPlatform } from "~/modules/shared/utils/sourceFilterOptions";
import type { AccountCompletionItem } from "./accountCompletion";
import type { QuestionContextListProps } from "./questionContextKeyboard";
import {
	type DocMentionData,
	questionBoxAccountMentionKey,
} from "./questionDocument";
import { isRecentCompletion } from "./RecentAccountCompletions";
import "./QuestionContextChannels.css";

const channelAvatarContainer = {
	size: "2.25rem",
	borderRadius: "50%",
	background: "gvt-blue-950",
} as const;

export type QuestionContextChannel = {
	type: "social_account" | "influencer";
	handle: string;
	platform?: string;
	id?: string;
	profilePictureUrl?: string | null;
	clipCount?: number | null;
};

type Props = QuestionContextListProps & {
	/** Surface the rows sit on, which the platform logos' palette has to match. */
	surface: "standard" | "inverse";
	channels: QuestionContextChannel[];
	/**
	 * Accounts matching the `@handle` being typed in the question. They take the
	 * place of `channels` while a mention is being written, so the tab lists what
	 * that handle could become.
	 */
	completions: AccountCompletionItem[];
	/** The `@handle` being written, or "" while browsing the pool. */
	searchQuery: string;
	/**
	 * Whether "" means browse this scope for accounts, like the Videos tab
	 * (see QuestionBoxProps.contextSource).
	 */
	browsesPool: boolean;
	/** @default "Channels" */
	channelsTabLabel?: "Channels" | "Sources" | "Creators";
	onSelectCompletion: (account: AccountCompletionItem) => void;
	insertedAccountMentionKeys: () => string[];
	insertAccountMentions: (mentions: DocMentionData[]) => void;
	canAddMore?: () => boolean;
};

const handleLabel = (handle: string) =>
	handle.startsWith("@") ? handle : `@${handle}`;

const rowKindLabel = (
	type: QuestionContextChannel["type"],
	platform?: string,
) => {
	if (type === "influencer") {
		return "Creator";
	}
	if (isDirectUploadPlatform(platform)) {
		return "Direct uploads";
	}
	return "Source";
};

const clipCountLabel = (count: number | null | undefined) => {
	const n = count == null || !Number.isFinite(count) ? 0 : count;
	const formatted = n.toLocaleString();
	return n === 1 ? `${formatted} clip` : `${formatted} clips`;
};

const channelForCompletion = (
	account: AccountCompletionItem,
): QuestionContextChannel => ({
	type: account.type ?? "social_account",
	handle: account.handle ?? "",
	platform: account.type === "social_account" ? account.platform : undefined,
	id: account.id,
	profilePictureUrl: account.profile_picture_url,
});

const bareHandle = (handle: string | null | undefined) =>
	(handle ?? "").trim().replace(/^@+/, "").toLowerCase();

const barePlatform = (platform: string | null | undefined) =>
	(platform ?? "").trim().toLowerCase();

/**
 * Whether two rows stand for the same account. The conversation's mentions and
 * the typeahead's hits come from different searches, so the same account can
 * arrive as an influencer on one side and as a channel on the other — hence
 * neither kind nor an exact mention key can be insisted on here. An account
 * with no platform of its own, an influencer, stands for its handle on all of
 * them.
 */
const isSameAccount = (
	a: QuestionContextChannel,
	b: QuestionContextChannel,
) => {
	if (a.id && b.id && a.id === b.id) {
		return true;
	}
	const handle = bareHandle(a.handle);
	if (handle.length === 0 || handle !== bareHandle(b.handle)) {
		return false;
	}
	const platformA = barePlatform(a.platform);
	const platformB = barePlatform(b.platform);
	return (
		platformA.length === 0 || platformB.length === 0 || platformA === platformB
	);
};

const channelSubtitle = (channel: QuestionContextChannel) =>
	`${rowKindLabel(channel.type, channel.platform)} · ${clipCountLabel(channel.clipCount)}`;

const completionSubtitle = (account: AccountCompletionItem) => {
	const parts = [
		rowKindLabel(
			account.type ?? "social_account",
			account.type === "social_account" ? account.platform : undefined,
		),
	];
	if (account.name && account.name !== account.handle) {
		parts.push(account.name);
	}
	if (isRecentCompletion(account)) {
		parts.push("Recent");
	}
	return parts.join(" · ");
};

const ChannelRow = (props: {
	surface: "standard" | "inverse";
	channel: QuestionContextChannel;
	subtitle: string;
	mentioned: boolean;
	added: boolean;
	blocked: boolean;
	highlighted: boolean;
	onSelect: () => void;
}) => {
	let rowRef: HTMLDivElement | undefined;

	createEffect(() => {
		if (props.highlighted) rowRef?.scrollIntoView({ block: "nearest" });
	});

	return (
		<div
			ref={rowRef}
			class="gvtui-question-context-channels__item"
			classList={{
				"gvtui-question-context-channels__item--highlighted": props.highlighted,
				"gvtui-question-context-channels__item--blocked": props.blocked,
			}}
			role="button"
			tabIndex={props.highlighted ? 0 : -1}
			aria-disabled={props.blocked || undefined}
			aria-label={
				props.added
					? `${handleLabel(props.channel.handle)} already in question`
					: props.blocked
						? "Can't add more sources"
						: `Add ${handleLabel(props.channel.handle)}`
			}
			onMouseDown={(event) => event.preventDefault()}
			onClick={() => {
				if (!props.blocked) {
					props.onSelect();
				}
			}}
			onKeyDown={(event) => {
				if (props.blocked) {
					return;
				}
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					props.onSelect();
				}
			}}
		>
			<HStack fullWidth align="center" justify="space-between" spacing="medium">
				<HStack grow align="center" spacing="medium">
					<Show
						when={isDirectUploadPlatform(props.channel.platform)}
						fallback={
							<Show
								when={props.channel.profilePictureUrl}
								fallback={
									<MaterialSymbol
										symbol="account_circle"
										color="blue"
										container={channelAvatarContainer}
									/>
								}
							>
								<img
									class="gvtui-question-context-channels__avatar"
									src={props.channel.profilePictureUrl ?? ""}
									alt=""
								/>
							</Show>
						}
					>
						<MaterialSymbol
							symbol="upload"
							color="blue"
							container={channelAvatarContainer}
						/>
					</Show>
					<VStack grow>
						<HStack align="center" spacing="small">
							<Text
								class="gvtui-question-context-channels__handle"
								color="inherit"
								ellipsis
								weight="bold"
							>
								{handleLabel(props.channel.handle)}
							</Text>
							<Show when={getPlatformColorGlyph(props.channel.platform)}>
								{(glyph) => (
									<GVTColorSymbol
										theme={props.surface}
										class="gvtui-question-context-channels__platform"
									>
										{glyph()}
									</GVTColorSymbol>
								)}
							</Show>
						</HStack>
						<HStack align="center" spacing="small">
							<MonoText color="fg3">{props.subtitle}</MonoText>
							<Show when={props.mentioned}>
								<Pill theme="grey600" size="xsmall" variant="outline">
									Mentioned
								</Pill>
							</Show>
						</HStack>
					</VStack>
				</HStack>
				<MaterialSymbol
					symbol={props.added ? "check" : "add"}
					color={props.added ? "success" : "fg3"}
					relativeSize="larger"
				/>
			</HStack>
		</div>
	);
};

export const QuestionContextChannels = (props: Props) => {
	/** Whether the conversation has mentioned the account a row offers. */
	const isMentioned = (channel: QuestionContextChannel) =>
		props.channels.some((mentioned) => isSameAccount(mentioned, channel));

	const isInserted = (channel: QuestionContextChannel) =>
		props
			.insertedAccountMentionKeys()
			.includes(questionBoxAccountMentionKey(channel));

	const canAddMore = () => props.canAddMore?.() ?? true;

	const addChannel = (channel: QuestionContextChannel) => {
		if (isInserted(channel) || !canAddMore()) {
			return;
		}
		props.insertAccountMentions([
			{
				type: channel.type,
				handle: channel.handle,
				platform: channel.platform,
				id: channel.id,
				avatarUrl: channel.profilePictureUrl,
			},
		]);
	};

	const selectCompletion = (account: AccountCompletionItem) => {
		if (isInserted(channelForCompletion(account)) || !canAddMore()) {
			return;
		}
		props.onSelectCompletion(account);
	};

	/**
	 * Search hits from the pool or a typed handle, rather than accounts already
	 * mentioned in the conversation.
	 */
	const listingCompletions = () => {
		if (props.completions.length > 0) {
			return true;
		}
		if (props.searchQuery.trim().length > 0) {
			return true;
		}
		return props.browsesPool && props.channels.length === 0;
	};

	const emptyListMessage = () => {
		const label = (props.channelsTabLabel ?? "Channels").toLowerCase();
		return props.searchQuery.trim().length > 0
			? `No ${label} match`
			: `No ${label} to add`;
	};

	props.registerList({
		count: () =>
			listingCompletions() ? props.completions.length : props.channels.length,
		select: (index) => {
			if (listingCompletions()) {
				const account = props.completions[index];
				if (account) selectCompletion(account);
				return;
			}
			const channel = props.channels[index];
			if (channel) addChannel(channel);
		},
	});

	return (
		<VStack fullWidth spacing="none">
			<Show
				when={listingCompletions()}
				fallback={
					<For each={props.channels}>
						{(channel, index) => (
							<ChannelRow
								surface={props.surface}
								channel={channel}
								subtitle={channelSubtitle(channel)}
								mentioned={isMentioned(channel)}
								added={isInserted(channel)}
								blocked={!isInserted(channel) && !canAddMore()}
								highlighted={index() === props.highlightedIndex}
								onSelect={() => addChannel(channel)}
							/>
						)}
					</For>
				}
			>
				<For each={props.completions}>
					{(account, index) => {
						const channel = channelForCompletion(account);
						return (
							<ChannelRow
								surface={props.surface}
								channel={channel}
								subtitle={completionSubtitle(account)}
								mentioned={isMentioned(channel)}
								added={isInserted(channel)}
								blocked={!isInserted(channel) && !canAddMore()}
								highlighted={index() === props.highlightedIndex}
								onSelect={() => selectCompletion(account)}
							/>
						);
					}}
				</For>
				<Show when={props.completions.length === 0}>
					<HStack fullWidth justify="center">
						<Text color="fg3">{emptyListMessage()}</Text>
					</HStack>
				</Show>
			</Show>
		</VStack>
	);
};
